// Imperative toast API backed by a single global queue.
//
// Use it instead of inline alert/notice banners:
//
//   toast.success("Saved.")
//   toast.error("Could not save the order.")
//   toast.info("Verification started.", { duration: 8000 })
//   toast.warning("Subscription is running low.")
//
// `ToastContainer` (mounted once globally) reads the queue and
// renders the items with enter/leave animations. Items auto-dismiss
// after `duration` ms (default 5000); pass `duration: 0` to make a
// toast sticky until the user dismisses it manually.
//
// Server-rendered `flash.notice` / `flash.alert` are forwarded to
// this queue automatically by `ToastContainer` — pages do not need
// to read flash directly anymore.

import { reactive, readonly } from "vue"

const items = reactive([])
let nextId = 1
const timers = new Map()

const VARIANTS = ["success", "danger", "warning", "info"]

function dismiss(id) {
  const index = items.findIndex((t) => t.id === id)
  if (index !== -1) items.splice(index, 1)
  const timer = timers.get(id)
  if (timer) {
    clearTimeout(timer)
    timers.delete(id)
  }
}

function push({ variant, message, duration }) {
  if (!message) return null
  if (!VARIANTS.includes(variant)) variant = "info"

  const id = nextId++
  const item = {
    id,
    variant,
    message,
    duration: duration ?? 5000,
  }
  items.push(item)

  if (item.duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismiss(id), item.duration),
    )
  }

  return id
}

export const toast = {
  success: (message, options = {}) =>
    push({ ...options, variant: "success", message }),
  error: (message, options = {}) =>
    push({ ...options, variant: "danger", message }),
  warning: (message, options = {}) =>
    push({ ...options, variant: "warning", message }),
  info: (message, options = {}) =>
    push({ ...options, variant: "info", message }),
  dismiss,
}

export function useToastQueue() {
  return { items: readonly(items), dismiss }
}
